import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import {
  maintenanceService,
  type Maintenance,
  type MaintenanceStatus,
} from '../../api/maintenance';
import {
  Wrench,
  Search,
  RefreshCw,
  Car,
  Building2,
  AlignLeft,
  CalendarDays,
  DollarSign,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../components/ui/SharedLayout';

const statusLabelMap: Record<MaintenanceStatus, string> = {
  pending: 'Pendente',
  active: 'Aprovada',
  rejected: 'Rejeitada',
  concluded: 'Concluída',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const ITEMS_PER_PAGE = 20;

const ManutencaoPage = () => {
  const { setPageTitle } = useAuth();
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMaintenance, setSelectedMaintenance] = useState<Maintenance | null>(null);

  useEffect(() => {
    setPageTitle('Manutenções do setor');
  }, [setPageTitle]);

  const fetchMaintenances = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await maintenanceService.getAll();
      setMaintenances(data);
    } catch (err: unknown) {
      setMaintenances([]);
      setError(getApiErrorMessage(err, 'Erro ao carregar manutenções do setor.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchMaintenances();
  }, [fetchMaintenances]);

  const filteredMaintenances = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return maintenances;
    return maintenances.filter((maintenance) =>
      [
        maintenance.vehicle?.brand || '',
        maintenance.vehicle?.description || '',
        maintenance.vehicle?.license_plate || '',
        maintenance.provider?.name || '',
        maintenance.reason || '',
        statusLabelMap[maintenance.status],
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [maintenances, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredMaintenances.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredMaintenances.length, currentPage, totalPages]);

  const { paginatedMaintenances, pageRange } = useMemo(() => {
    const total = filteredMaintenances.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredMaintenances.slice(startIndex, endIndex);
    return {
      paginatedMaintenances: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredMaintenances, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const renderStatusBadge = (status: MaintenanceStatus) => {
    switch (status) {
      case 'active':
        return (
          <Badge tone="blue" className="gap-1.5">
            <Wrench className="h-3 w-3 stroke-[1.9]" />
            {statusLabelMap[status]}
          </Badge>
        );
      case 'concluded':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            {statusLabelMap[status]}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            {statusLabelMap[status]}
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[1.9]" />
            {statusLabelMap[status]}
          </Badge>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Manutenções do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Manutenções
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Acompanhe as manutenções
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                dos veículos do seu departamento
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button variant="secondary" onClick={() => void fetchMaintenances()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-slate-600 via-blue-700 to-indigo-800 text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,0.6)]">
                <Wrench className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Manutenções
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhuma manutenção encontrada'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} manutenções listadas (total cadastrado: ${maintenances.length})`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-maintenances"
                placeholder="Pesquisar veículo, placa, fornecedor, motivo ou status..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando manutenções...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Fornecedor</Th>
                      <Th>Motivo</Th>
                      <Th>Data de Entrada</Th>
                      <Th>Previsão de Conclusão</Th>
                      <Th>Preço Final</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhuma manutenção encontrada
                        </td>
                      </tr>
                    ) : (
                      paginatedMaintenances.map((m) => (
                        <Tr key={m.id}>
                          <Td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-slate-600 via-blue-700 to-indigo-800 text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,0.55)]">
                                <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-slate-900" title={m.vehicle?.brand}>
                                  {m.vehicle?.brand || `Veículo #${m.vehicle_id}`}
                                </div>
                                <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                  {m.vehicle?.license_plate || '-'}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="truncate font-medium text-slate-700" title={m.provider?.name}>
                                {m.provider?.name || `Fornecedor #${m.provider_id}`}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-start gap-1.5 w-full">
                              <AlignLeft className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                              <span className="block truncate font-medium text-slate-700 max-w-[300px]" title={m.reason}>
                                {m.reason || '-'}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                {formatDate(m.entry_date)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                {formatDate(m.estimated_completion)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <DollarSign className="h-3.5 w-3.5 stroke-[1.9] text-emerald-600" />
                              <span className="text-[13px] font-bold text-emerald-700 tabular-nums">
                                {formatCurrency(m.final_price)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            {renderStatusBadge(m.status)}
                          </Td>
                          <Td>
                            <div className="flex items-center gap-1.5">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setSelectedMaintenance(m)}
                                className="!h-8 !px-2"
                                title="Visualizar detalhes da manutenção"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>
      </div>

      {selectedMaintenance && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">
                    Detalhes da Manutenção
                  </h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Registro #{selectedMaintenance.id} • Atualizado em {formatDateTime(selectedMaintenance.updated_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMaintenance(null)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3.5">
                    <div className="mr-1 text-[12.5px] font-bold uppercase tracking-wide text-slate-500">
                      Situação atual:
                    </div>
                    {renderStatusBadge(selectedMaintenance.status)}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Veículo</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">
                      {selectedMaintenance.vehicle?.brand || `Veículo #${selectedMaintenance.vehicle_id}`}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-slate-600 font-mono">
                      {selectedMaintenance.vehicle?.license_plate || '-'}
                    </p>
                    {selectedMaintenance.vehicle?.description && (
                      <p className="mt-1 text-[12px] text-slate-500 truncate">{selectedMaintenance.vehicle.description}</p>
                    )}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Fornecedor / Oficina</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">
                      {selectedMaintenance.provider?.name || `Fornecedor #${selectedMaintenance.provider_id}`}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Data de Entrada</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">
                      {formatDate(selectedMaintenance.entry_date)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Previsão de Conclusão</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">
                      {formatDate(selectedMaintenance.estimated_completion)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-emerald-700">Custo Total (Preço Final)</p>
                    <p className="mt-1.5 text-[18px] font-black text-emerald-800 tabular-nums">
                      {formatCurrency(selectedMaintenance.final_price)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Solicitado / Cadastrado por</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">
                      {selectedMaintenance.created_by_user?.name || '-'}
                    </p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                      <span className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">
                        {selectedMaintenance.created_by_user?.role || '-'}
                      </span>
                      <span className="text-[11.5px] text-slate-400 tabular-nums">
                        • Cadastrado em {formatDateTime(selectedMaintenance.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Motivo / Descrição Completa do Serviço</p>
                    <p className="mt-1.5 text-[14px] font-medium text-slate-800 whitespace-pre-wrap leading-relaxed">
                      {selectedMaintenance.reason || 'Nenhuma descrição cadastrada para esta manutenção.'}
                    </p>
                  </div>
                </div>
                <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setSelectedMaintenance(null)}
                    className="h-10 px-5"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManutencaoPage;
